import type { ProposalExtraItemType, ProposalProductItemType, ProductSetFullType, ProductType, ServiceType } from '@econtractor/types';
import * as apiProduct from 'api/apiProducts';
import * as apiProductSet from 'api/apiProductSets';
import * as apiService from 'api/apiServices';
import { safeIntParse } from 'utils/helpers/safeParsing';

const uniquePositiveIds = (ids: Array<number | undefined | null>) =>
    Array.from(
        new Set(
            ids
                .map((id) => safeIntParse(id || 0) || 0)
                .filter((id) => id > 0),
        ),
    );

const hasDimension = (row: { dimensionName?: string; dimensionI18nKey?: string }) =>
    Boolean(String(row.dimensionName || '').trim() || String(row.dimensionI18nKey || '').trim());

/** Fills unit of measure from the catalog when a saved line has none. */
export const fillMissingLineDimensions = async (
    products: ProposalProductItemType[],
    additionalItems: ProposalExtraItemType[],
): Promise<{ products: ProposalProductItemType[]; additionalItems: ProposalExtraItemType[] }> => {
    const productIds = uniquePositiveIds(
        products
            .filter((row) => !row.asSetUnit && !hasDimension(row))
            .map((row) => row.productId),
    );
    const serviceIds = uniquePositiveIds(
        additionalItems.filter((row) => !hasDimension(row)).map((row) => row.serviceId),
    );
    const setIds = uniquePositiveIds(
        products.filter((row) => row.asSetUnit && !hasDimension(row)).map((row) => row.sourceId),
    );

    const productsMap: Record<number, ProductType> = {};
    const servicesMap: Record<number, ServiceType> = {};
    const setsMap: Record<number, ProductSetFullType> = {};

    if (setIds.length) {
        const sets = await Promise.all(setIds.map((setId) => apiProductSet.getProductSet(setId)));
        sets.forEach((set, index) => {
            if (set) setsMap[setIds[index]] = set;
        });
    }

    if (productIds.length) {
        const productsRes = await apiProduct.getList({
            Ids: productIds,
            onPage: productIds.length,
        });
        (productsRes?.products || []).forEach((product) => {
            productsMap[Number(product.ID)] = product;
        });
    }

    if (serviceIds.length) {
        const servicesRes = await apiService.getList({
            Ids: serviceIds,
            onPage: serviceIds.length,
        });
        (servicesRes?.services || []).forEach((service) => {
            servicesMap[Number(service.ID)] = service;
        });
    }

    return {
        products: products.map((row) => {
            if (hasDimension(row)) return row;
            const productId = safeIntParse(row.productId || 0) || 0;
            const product = row.asSetUnit
                ? setsMap[safeIntParse(row.sourceId || 0) || 0]
                : productsMap[productId];
            if (!product || !hasDimension(product)) return row;
            return {
                ...row,
                dimensionName: String(product.dimensionName || ''),
                dimensionI18nKey: String(product.dimensionI18nKey || ''),
            };
        }),
        additionalItems: additionalItems.map((row) => {
            if (hasDimension(row)) return row;
            const serviceId = safeIntParse(row.serviceId || 0) || 0;
            const service = servicesMap[serviceId];
            if (!service || !hasDimension(service)) return row;
            return {
                ...row,
                dimensionName: String(service.dimensionName || ''),
                dimensionI18nKey: String(service.dimensionI18nKey || ''),
            };
        }),
    };
};
